import { useState } from 'react'
import { DriverAnalysis, DailySummary, ActivityRecord } from '../types'

const STATUS_BADGE: Record<string, string> = {
  ok: 'bg-green-50 text-green-700 border-green-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  infringement: 'bg-red-50 text-red-700 border-red-200',
}

const ACTIVITY_LABEL: Record<string, string> = {
  driving: 'Driving',
  work: 'Other Work',
  rest: 'Break / Rest',
  availability: 'Availability (POA)',
}

const ACTIVITY_COLOR: Record<string, string> = {
  driving: 'bg-blue-700',
  work: 'bg-amber-600',
  rest: 'bg-green-600',
  availability: 'bg-slate-400',
}

const ACTIVITY_BAR_COLOR: Record<string, string> = {
  driving: '#1d4ed8',
  work: '#d97706',
  rest: '#16a34a',
  availability: '#94a3b8',
}

export function DailyTab({ analysis }: { analysis: DriverAnalysis }) {
  const { daily_breakdown } = analysis
  const [expandedDay, setExpandedDay] = useState<string | null>(null)

  return (
    <div>
      {/* Legend */}
      <div className="flex items-center gap-4 text-[11px] text-gray-400 mb-3 pb-2 border-b border-gray-100">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-blue-700 rounded-sm" /> Driving</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-amber-600 rounded-sm" /> Other Work</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-green-600 rounded-sm" /> Break / Rest</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-slate-400 rounded-sm" /> Availability (POA)</span>
        <span className="ml-auto text-gray-300">Click a day to expand activity detail</span>
      </div>

      {/* Table header */}
      <div className="grid grid-cols-[100px_1fr_65px_55px_55px_65px_55px_70px] gap-2 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-200">
        <span>Date</span>
        <span>Activity Timeline</span>
        <span className="text-right">Driving</span>
        <span className="text-right">Work</span>
        <span className="text-right">Rest</span>
        <span className="text-right">Distance</span>
        <span className="text-right">Cont. Drv</span>
        <span className="text-center">Status</span>
      </div>

      {/* Daily rows */}
      {daily_breakdown.map((day) => (
        <DayRow
          key={day.date}
          day={day}
          isExpanded={expandedDay === day.date}
          onToggle={() => setExpandedDay(expandedDay === day.date ? null : day.date)}
        />
      ))}

      {daily_breakdown.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">No daily data available</div>
      )}
    </div>
  )
}

function DayRow({ day, isExpanded, onToggle }: { day: DailySummary; isExpanded: boolean; onToggle: () => void }) {
  const total = day.driving_minutes + day.work_minutes + day.rest_minutes + day.availability_minutes
  const maxMins = Math.max(total, 960)

  const dayOfWeek = new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short' })
  const dateStr = new Date(day.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
  const statusBadge = STATUS_BADGE[day.status] || STATUS_BADGE.ok

  const hasActivities = day.activities && day.activities.length > 0
  const hasWtdIssue = (day.max_continuous_work_minutes || 0) > 360

  return (
    <div className={`border-b border-gray-100 ${day.status === 'infringement' ? 'bg-red-50/30' : ''}`}>
      {/* Summary row */}
      <button
        onClick={onToggle}
        className="w-full grid grid-cols-[100px_1fr_65px_55px_55px_65px_55px_70px] gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors items-center"
      >
        {/* Date */}
        <div>
          <span className="text-sm font-medium text-gray-900">{dayOfWeek}</span>
          <span className="text-xs text-gray-400 ml-1.5">{dateStr}</span>
          {day.vehicles && day.vehicles.length > 0 && (
            <p className="text-[10px] text-blue-600 font-medium truncate">{day.vehicles.map(v => v.reg).join(', ')}</p>
          )}
        </div>

        {/* Activity bar */}
        <div className="relative">
          <div className="flex h-5 rounded-sm overflow-hidden bg-gray-100">
            {day.driving_minutes > 0 && (
              <div className="bg-blue-700" style={{ width: `${(day.driving_minutes / maxMins) * 100}%` }} title={`Driving: ${fmtMin(day.driving_minutes)}`} />
            )}
            {day.work_minutes > 0 && (
              <div className="bg-amber-600" style={{ width: `${(day.work_minutes / maxMins) * 100}%` }} title={`Work: ${fmtMin(day.work_minutes)}`} />
            )}
            {day.rest_minutes > 0 && (
              <div className="bg-green-600" style={{ width: `${(day.rest_minutes / maxMins) * 100}%` }} title={`Rest: ${fmtMin(day.rest_minutes)}`} />
            )}
            {day.availability_minutes > 0 && (
              <div className="bg-slate-400" style={{ width: `${(day.availability_minutes / maxMins) * 100}%` }} title={`Availability: ${fmtMin(day.availability_minutes)}`} />
            )}
          </div>
          {/* 9h driving limit marker */}
          <div
            className="absolute top-0 bottom-0 border-l border-dashed border-red-300"
            style={{ left: `${Math.min((540 / maxMins) * 100, 100)}%` }}
          />
        </div>

        {/* Stats */}
        <span className={`text-sm text-right font-medium ${day.driving_hours > 9 ? 'text-red-600' : 'text-gray-900'}`}>
          {day.driving_hours}h
        </span>
        <span className="text-xs text-right text-gray-500">{fmtMin(day.work_minutes)}</span>
        <span className="text-xs text-right text-gray-500">{fmtMin(day.rest_minutes)}</span>
        <span className="text-xs text-right text-gray-500">{day.distance_km > 0 ? `${day.distance_km} km` : '—'}</span>
        <span className={`text-xs text-right ${day.max_continuous_driving_minutes > 270 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
          {fmtMin(day.max_continuous_driving_minutes)}
        </span>
        <span className="text-center">
          <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded border ${statusBadge}`}>
            {day.status === 'ok' ? 'OK' : day.status === 'warning' ? 'WARN' : 'INFR'}
          </span>
        </span>
      </button>

      {/* WTD warning line */}
      {hasWtdIssue && !isExpanded && (
        <div className="px-3 pb-1.5 -mt-1">
          <p className="text-[10px] text-red-600 ml-[100px]">
            WTD: {fmtMin(day.max_continuous_work_minutes || 0)} continuous work without 30-min break (limit 6h)
          </p>
        </div>
      )}

      {/* Expanded detail */}
      {isExpanded && hasActivities && (
        <div className="px-3 pb-3 pt-1">
          <div className="ml-[100px] border border-gray-200 rounded overflow-hidden">
            {/* Activity timeline header */}
            <div className="grid grid-cols-[140px_110px_70px_70px_1fr] gap-0 bg-gray-50 border-b border-gray-200">
              <span className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Time</span>
              <span className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Activity</span>
              <span className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 text-right">Duration</span>
              <span className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 text-center">Card</span>
              <span className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Timeline</span>
            </div>
            {/* Activity rows */}
            {day.activities!.map((act, i) => (
              <ActivityRow key={i} act={act} dayTotal={maxMins} />
            ))}
            {/* Day totals */}
            <div className="grid grid-cols-[140px_110px_70px_70px_1fr] gap-0 bg-gray-50 border-t border-gray-200">
              <span className="px-3 py-1.5 text-[10px] font-semibold text-gray-500">TOTALS</span>
              <span className="px-3 py-1.5 text-[10px] text-gray-400" />
              <span className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 text-right">{fmtMin(total)}</span>
              <span className="px-3 py-1.5" />
              <span className="px-3 py-1.5 text-[10px] text-gray-400">
                Driving: {fmtMin(day.driving_minutes)} · Work: {fmtMin(day.work_minutes)} · Rest: {fmtMin(day.rest_minutes)} · POA: {fmtMin(day.availability_minutes)}
              </span>
            </div>
          </div>

          {/* Compliance notes below table */}
          <div className="ml-[100px] mt-2 space-y-1">
            {day.max_continuous_driving_minutes > 240 && (
              <p className={`text-[11px] ${day.max_continuous_driving_minutes > 270 ? 'text-red-600 font-medium' : 'text-amber-600'}`}>
                EC 561: Max continuous driving {fmtMin(day.max_continuous_driving_minutes)}
                {day.max_continuous_driving_minutes > 270 && ' — exceeds 4.5h limit (requires 45-min break)'}
              </p>
            )}
            {(day.max_continuous_work_minutes || 0) > 300 && (
              <p className={`text-[11px] ${(day.max_continuous_work_minutes || 0) > 360 ? 'text-red-600 font-medium' : 'text-amber-600'}`}>
                WTD: Max continuous work {fmtMin(day.max_continuous_work_minutes || 0)}
                {(day.max_continuous_work_minutes || 0) > 360 && ' — exceeds 6h limit (requires 30-min break)'}
              </p>
            )}
          </div>
        </div>
      )}

      {isExpanded && !hasActivities && (
        <div className="px-3 pb-3 pt-1">
          <p className="text-xs text-gray-400 ml-[100px]">No granular activity data available for this day. Re-download the driver card to populate detail records.</p>
        </div>
      )}
    </div>
  )
}

function ActivityRow({ act, dayTotal }: { act: ActivityRecord; dayTotal: number }) {
  const startTime = act.start_time ? formatTime(act.start_time) : '—'
  const endTime = act.end_time ? formatTime(act.end_time) : '—'
  const label = ACTIVITY_LABEL[act.activity_type] || act.activity_type
  const barColor = ACTIVITY_BAR_COLOR[act.activity_type] || '#94a3b8'

  // Calculate position on timeline
  const startMins = act.start_time ? getMinutesOfDay(act.start_time) : 0
  const durMins = act.duration_minutes || 0
  const leftPct = (startMins / dayTotal) * 100
  const widthPct = Math.max((durMins / dayTotal) * 100, 0.5) // min 0.5% for visibility

  const isWork = act.activity_type === 'driving' || act.activity_type === 'work'
  const rowBg = act.activity_type === 'driving' ? 'bg-blue-50/40' :
                act.activity_type === 'work' ? 'bg-amber-50/30' : ''

  return (
    <div className={`grid grid-cols-[140px_110px_70px_70px_1fr] gap-0 border-b border-gray-50 hover:bg-gray-50/50 ${rowBg}`}>
      <span className="px-3 py-1 text-xs text-gray-700 font-mono">
        {startTime} — {endTime}
      </span>
      <span className="px-3 py-1 flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-sm flex-shrink-0 ${ACTIVITY_COLOR[act.activity_type] || 'bg-gray-400'}`} />
        <span className={`text-xs ${isWork ? 'font-medium text-gray-800' : 'text-gray-500'}`}>{label}</span>
      </span>
      <span className={`px-3 py-1 text-xs text-right ${isWork ? 'font-medium text-gray-800' : 'text-gray-400'}`}>
        {fmtMin(durMins)}
      </span>
      <span className="px-3 py-1 text-center">
        {act.card_status === 'not_inserted' && (
          <span className="text-[9px] text-red-500 font-medium">NO CARD</span>
        )}
      </span>
      <span className="px-3 py-1">
        <div className="relative h-3 bg-gray-100 rounded-sm overflow-hidden">
          <div
            className="absolute top-0 bottom-0 rounded-sm"
            style={{
              left: `${leftPct}%`,
              width: `${widthPct}%`,
              backgroundColor: barColor,
              opacity: 0.8,
            }}
          />
        </div>
      </span>
    </div>
  )
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  } catch {
    return '—'
  }
}

function getMinutesOfDay(iso: string): number {
  try {
    const d = new Date(iso)
    return d.getHours() * 60 + d.getMinutes()
  } catch {
    return 0
  }
}

function fmtMin(mins: number): string {
  if (!mins && mins !== 0) return '—'
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h > 0 ? `${h}h${m > 0 ? `${m.toString().padStart(2, '0')}` : ''}` : `${m}m`
}
